import { z } from 'zod';
import { IsoTimestamp, SchoolId } from './common.js';
/**
 * Academic master data sync (AIMSIS-18807).
 *
 * A separate, clearly scoped write operation alongside the roster sync. It
 * touches only the academic reference masters — Grade Levels, Subjects, and
 * Classrooms — and never writes Users, user.subjectIds, user.classroomIds,
 * Class Memberships, Teaching Assignments, Enrollments, Quizzes, or Reports.
 *
 * Ordering is enforced by the API: the apply endpoint refuses to run until a
 * preview for the same tenant has been produced, and any source change since
 * that preview invalidates it.
 */
export const AcademicMasterDataset = z.enum([
    'gradeLevels',
    'subjects',
    'classrooms',
    'classMemberships',
    'teachingAssignments',
]);
export const AcademicMasterOutcome = z.enum([
    'created',
    'updated',
    'unchanged',
    'archived',
    'skipped',
    'failed',
]);
export const AcademicMasterCounts = z.object({
    fetched: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
export const AcademicMasterRecord = z.object({
    dataset: AcademicMasterDataset,
    /** Matched record: the internal CBT document id. */
    internalId: z.string().nullable(),
    /** AIMSIS identity used for matching. Null when the source row has no usable ID. */
    externalId: z.string().nullable(),
    /** Readable source name, or the internal name for an archived record. */
    name: z.string().nullable(),
    sourceStatus: z.enum(['active', 'archived']),
    outcome: AcademicMasterOutcome,
    /** Populated for `skipped` and `failed` records. */
    reason: z.string().nullable(),
});
export const AcademicMasterDatasetResult = z.object({
    dataset: AcademicMasterDataset,
    counts: AcademicMasterCounts,
    records: z.array(AcademicMasterRecord),
});
/** Which AIMSIS source produced this preview. */
export const AcademicMasterSource = z.object({
    system: z.literal('aimsis'),
    academicDbName: z.string().min(1),
    cbtSubdomain: z.string().min(1),
    academicSubdomain: z.string(),
    /** Active AIMSIS year/period names — source metadata only, never CBT master records. */
    activeSchoolYear: z.string().nullable(),
    activeSchoolPeriod: z.string().nullable(),
});
export const AcademicMasterSyncResult = z.object({
    schoolId: SchoolId,
    dryRun: z.boolean(),
    /** `null` for a preview, whose result carries no run id. */
    runId: z.string().nullable(),
    previewId: z.string().min(1),
    source: AcademicMasterSource,
    /** ISO timestamp of the AIMSIS read this result was derived from. */
    sourceReadAt: IsoTimestamp,
    syncedAt: IsoTimestamp,
    datasets: z.array(AcademicMasterDatasetResult),
    /**
     * Datasets whose AIMSIS source read failed. Non-empty means this preview cannot
     * be applied: apply re-reads the source and refuses while any read fails. This is
     * a UX gate only, not authorization. Row-level `failed` counts do not appear here.
     */
    failedSourceDatasets: z.array(AcademicMasterDataset),
    warnings: z.array(z.string()),
});
/** Body for POST /v1/sync/academic-master/apply — binds apply to the reviewed preview. */
export const AcademicMasterApplyRequest = z.object({
    previewId: z.string().min(1),
});
/** 409 body when an apply is attempted without a usable preview. */
export const AcademicMasterPreviewRequired = z.object({
    error: z.literal('PreviewRequired'),
    message: z.string(),
});
//# sourceMappingURL=academic-master-sync.js.map